import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';

export default function SubmitAppPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    appName: '',
    appCategory: '',
    appStoreLink: '',
    prototypeLink: '',
    description: '',
    stage: '',
    focusArea: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      setFormData({
        fullName: '',
        email: '',
        appName: '',
        appCategory: '',
        appStoreLink: '',
        prototypeLink: '',
        description: '',
        stage: '',
        focusArea: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-700 text-white py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Submit Your App for Evaluation
          </h1>
          <p className="text-xl md:text-2xl text-white/90">
            After checkout, enter your app details here to begin the intelligence evaluation process.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                  <i className="ri-check-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-green-900 mb-2">
                    Thanks for submitting your app!
                  </h3>
                  <p className="text-green-800">
                    Our team will review your details and follow up with next steps.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
                  placeholder="john@example.com"
                />
              </div>

              {/* App Name */}
              <div>
                <label htmlFor="appName" className="block text-sm font-semibold text-gray-700 mb-2">
                  App Name *
                </label>
                <input
                  type="text"
                  id="appName"
                  name="appName"
                  value={formData.appName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
                  placeholder="My Awesome App"
                />
              </div>

              {/* App Category */}
              <div>
                <label htmlFor="appCategory" className="block text-sm font-semibold text-gray-700 mb-2">
                  App Category *
                </label>
                <input
                  type="text"
                  id="appCategory"
                  name="appCategory"
                  value={formData.appCategory}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
                  placeholder="e.g., Productivity, Social, Finance"
                />
              </div>

              {/* App Store Link */}
              <div>
                <label htmlFor="appStoreLink" className="block text-sm font-semibold text-gray-700 mb-2">
                  App Store Link (if live)
                </label>
                <input
                  type="url"
                  id="appStoreLink"
                  name="appStoreLink"
                  value={formData.appStoreLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
                  placeholder="https://apps.apple.com/..."
                />
              </div>

              {/* Prototype Link */}
              <div>
                <label htmlFor="prototypeLink" className="block text-sm font-semibold text-gray-700 mb-2">
                  Prototype Link (if early stage)
                </label>
                <input
                  type="url"
                  id="prototypeLink"
                  name="prototypeLink"
                  value={formData.prototypeLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base"
                  placeholder="https://figma.com/..."
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                  Short Description (What does your app do?) *
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base resize-none"
                  placeholder="Describe your app in a few sentences..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.description.length}/500 characters
                </p>
              </div>

              {/* Stage */}
              <div>
                <label htmlFor="stage" className="block text-sm font-semibold text-gray-700 mb-2">
                  What stage are you in? *
                </label>
                <select
                  id="stage"
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base cursor-pointer"
                >
                  <option value="">Select a stage</option>
                  <option value="idea">Idea</option>
                  <option value="prototype">Prototype</option>
                  <option value="launched">Launched</option>
                  <option value="scaling">Scaling</option>
                </select>
              </div>

              {/* Focus Area */}
              <div>
                <label htmlFor="focusArea" className="block text-sm font-semibold text-gray-700 mb-2">
                  Anything specific you want us to focus on? (Optional)
                </label>
                <textarea
                  id="focusArea"
                  name="focusArea"
                  value={formData.focusArea}
                  onChange={handleChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none text-base resize-none"
                  placeholder="Any specific areas you'd like us to focus on..."
                />
                <p className="text-sm text-gray-500 mt-1">
                  {formData.focusArea.length}/500 characters
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-lg font-semibold hover:from-teal-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Submit My App'}
              </button>

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                  <p className="text-red-800 font-semibold text-center">
                    ✗ Something went wrong. Please try again.
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* What Happens Next */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16 text-gray-900">
            What Happens Next
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-inbox-fill',
                title: 'Your app enters the evaluation queue'
              },
              {
                icon: 'ri-brain-fill',
                title: 'We run your selected tier\'s analysis'
              },
              {
                icon: 'ri-mail-check-fill',
                title: 'You receive a confirmation email'
              },
              {
                icon: 'ri-notification-fill',
                title: 'You get updates as major stages are completed'
              },
              {
                icon: 'ri-file-pdf-fill',
                title: 'Your PDF report is delivered within the promised timeframe'
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-100 to-blue-100 rounded-full mx-auto mb-4">
                  <i className={`${step.icon} text-3xl text-teal-600`}></i>
                </div>
                <p className="text-lg text-gray-700 font-medium">{step.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer"
            >
              Return to Homepage
            </Link>
            <Link
              to="/sample-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Download Sample Report
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
